/**
 * The booking panel's steps, as the booking site counts them and as Layer
 * stores them (`booking_panel_step_counts`, migration 225).
 *
 * ONE closed vocabulary, three readers:
 *
 *   - the receiver's `panel-step` member-API arm refuses any name that is not
 *     in it, so nothing but these strings can ever become a row key;
 *   - the dashboard `/analytics` page takes its row order and its labels from
 *     it;
 *   - the booking site (a separate repository) sends exactly these strings,
 *     the same ones it sends to GA4 as the `panel_step` event's content id.
 *
 * The site cannot import this file, so the contract is held from the other
 * side: a name the site sends that is not listed here is NOT counted and the
 * receiver logs `uncounted:unknown_step`. Adding a step is a change here
 * first, deployed before the site starts to send it.
 *
 * Two kinds of step, and they are counted differently BY THE SITE:
 *
 *   - a `screen` is counted every time the screen is put in front of the
 *     visitor. Back, a return from the account view and a reopened panel each
 *     count again.
 *   - a `moment` is something that happened inside a screen, counted at most
 *     once per opening of the panel. Compare moments with moments: each
 *     screen that has moments also has a `…_shown` moment to divide by.
 *
 * Pure data and pure functions, no imports: the dashboard page bundles it.
 */

export type BookingPanelStepKind = "screen" | "moment";

export interface BookingPanelStepDef {
  readonly step: string;
  readonly kind: BookingPanelStepKind;
  /** What the dashboard calls it. Plain words, no step ids. */
  readonly label: string;
}

/**
 * Every step, in the order a visitor meets them. The order is what the
 * dashboard renders, so a new step goes where it happens, not at the end.
 */
export const BOOKING_PANEL_STEPS: ReadonlyArray<BookingPanelStepDef> = [
  { step: "action_selection", kind: "screen", label: "Start screen" },
  { step: "membership_status", kind: "screen", label: "New or existing" },
  { step: "session_type", kind: "screen", label: "Studio or home" },
  { step: "onboarding", kind: "screen", label: "Questions before booking" },

  { step: "calendar", kind: "screen", label: "Calendar" },
  { step: "calendar_shown", kind: "moment", label: "Calendar seen" },
  { step: "calendar_day", kind: "moment", label: "Day tapped" },
  { step: "calendar_first_day_times", kind: "moment", label: "First day had times" },
  { step: "calendar_first_day_none", kind: "moment", label: "First day had no times" },
  { step: "calendar_first_day_error", kind: "moment", label: "First day failed to load" },
  { step: "calendar_show_all", kind: "moment", label: "Show all available slots tapped" },
  { step: "calendar_time", kind: "moment", label: "Time tapped" },
  { step: "calendar_confirm_unknown", kind: "moment", label: "Confirm tapped, not signed in" },
  { step: "calendar_confirm_known", kind: "moment", label: "Confirm tapped, signed in" },

  { step: "package_selection", kind: "screen", label: "Package choice" },

  { step: "email_collection", kind: "screen", label: "Phone or email entry" },
  { step: "phone_shown", kind: "moment", label: "Phone screen seen" },
  { step: "phone_entered", kind: "moment", label: "Number entered" },
  { step: "phone_invalid", kind: "moment", label: "Number not valid" },
  { step: "phone_refused", kind: "moment", label: "Number refused" },
  { step: "phone_confirm_shown", kind: "moment", label: "Asked to confirm the number" },
  { step: "phone_to_email", kind: "moment", label: "Switched to email" },
  { step: "phone_sent", kind: "moment", label: "Number accepted" },
  { step: "email_shown", kind: "moment", label: "Email screen seen" },
  { step: "email_sent", kind: "moment", label: "Email accepted" },

  { step: "name_collection", kind: "screen", label: "Name entry" },

  { step: "email_verification", kind: "screen", label: "Code entry" },
  { step: "code_shown", kind: "moment", label: "Code screen seen" },
  { step: "code_wrong", kind: "moment", label: "Wrong code" },
  { step: "code_ok", kind: "moment", label: "Code accepted" },

  { step: "checkout_email", kind: "screen", label: "Email for checkout" },
  { step: "checkout_email_code", kind: "screen", label: "Code for the checkout email" },
  { step: "stripe_payment", kind: "screen", label: "Payment" },
  { step: "home_request_details", kind: "screen", label: "At-home request details" },
  { step: "confirmation", kind: "screen", label: "Closing screen" },

  { step: "manage_booking_list", kind: "screen", label: "Booking list" },
  { step: "manage_options", kind: "screen", label: "Options for a booking" },
  { step: "reschedule_calendar", kind: "screen", label: "Calendar for a move" },
  { step: "question_input", kind: "screen", label: "Question box" },
];

const STEP_BY_NAME: ReadonlyMap<string, BookingPanelStepDef> = new Map(
  BOOKING_PANEL_STEPS.map((def) => [def.step, def]),
);

export function isBookingPanelStep(value: unknown): value is string {
  return typeof value === "string" && STEP_BY_NAME.has(value);
}

export function bookingPanelStepDef(step: string): BookingPanelStepDef | undefined {
  return STEP_BY_NAME.get(step);
}

/**
 * What the visitor came to do. The same screen serves several flows (the
 * phone screen is a new visitor's sign-up, a member's login and a buyer's
 * checkout alike), so a count without its flow cannot be read as a funnel.
 *
 * `none` is the start screen, before a choice is made.
 */
export interface BookingPanelFlowDef {
  readonly flow: string;
  readonly label: string;
}

export const BOOKING_PANEL_FLOWS: ReadonlyArray<BookingPanelFlowDef> = [
  { flow: "trial", label: "Trial booking" },
  { flow: "booking", label: "New visitor booking" },
  { flow: "member_booking", label: "Member booking" },
  { flow: "home_request", label: "At-home request" },
  { flow: "payment", label: "Buying a package" },
  { flow: "manage", label: "Managing a booking" },
  { flow: "question", label: "Asking a question" },
  { flow: "none", label: "Start screen, no choice yet" },
];

const FLOW_BY_NAME: ReadonlyMap<string, BookingPanelFlowDef> = new Map(
  BOOKING_PANEL_FLOWS.map((def) => [def.flow, def]),
);

export function isBookingPanelFlow(value: unknown): value is string {
  return typeof value === "string" && FLOW_BY_NAME.has(value);
}

export function bookingPanelFlowDef(flow: string): BookingPanelFlowDef | undefined {
  return FLOW_BY_NAME.get(flow);
}

/**
 * The kind of browser a count came from. The four names are the ones the
 * migration's CHECK constraint lists; a fifth needs a migration.
 */
export type PanelVisitorClass = "instagram_app" | "facebook_app" | "other_mobile" | "desktop";

export interface PanelVisitorClassDef {
  readonly visitorClass: PanelVisitorClass;
  readonly label: string;
}

export const PANEL_VISITOR_CLASSES: ReadonlyArray<PanelVisitorClassDef> = [
  { visitorClass: "instagram_app", label: "Instagram app" },
  { visitorClass: "facebook_app", label: "Facebook app" },
  { visitorClass: "other_mobile", label: "Other mobile" },
  { visitorClass: "desktop", label: "Desktop" },
];

// `bot` only where a word starts or ends with it (Googlebot, bingbot/2.1,
// AhrefsBot), never inside one: a phone that names its maker in the browser
// string (CUBOT_X30) is a visitor.
const NOT_A_VISITOR =
  /\bbot|bots?\b|crawl|spider|headless|lighthouse|facebookexternalhit|inspectiontool|preview|monitor|curl|python|node-fetch|axios|playwright/i;

/**
 * Class one request by its User-Agent header, or `null` when the request is
 * not a visitor and must not be counted: no header at all, a crawler or a
 * script, or one of our own proof runs (which name themselves `ProofWalk`).
 *
 * Every browser a visitor can use, the in-app ones included, opens its
 * string with `Mozilla/`. A caller that does not is a program, whatever it
 * calls itself, so that is checked before the list of known crawler words.
 *
 * The string is read for this one answer and is never stored or logged.
 */
export function classifyPanelVisitor(userAgent: string | undefined): PanelVisitorClass | null {
  if (userAgent === undefined || !userAgent.startsWith("Mozilla/")) return null;
  if (/ProofWalk/i.test(userAgent)) return null;
  if (NOT_A_VISITOR.test(userAgent)) return null;
  if (/Instagram/i.test(userAgent)) return "instagram_app";
  if (/FBAN|FBAV|FB_IAB/i.test(userAgent)) return "facebook_app";
  if (/Mobile|Android|iPhone|iPad/i.test(userAgent)) return "other_mobile";
  return "desktop";
}
